import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Search,
  Shield,
  ShieldCheck,
  Save,
  CheckSquare,
  Square,
  User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { userService } from '@/services/user.service';
import { permissionService } from '@/services/permission.service';
import { menuItems as adminMenuItems } from '@/components/layout/Sidebar';
import { learnerSidebarPermissionKeys } from '@/components/layout/LearnerSidebar';

interface PermissionItem {
  _id: string;
  permission_key: string;
  permission_name: string;
  description?: string;
}

interface CategoryGroup {
  category_key: string;
  category_label: string;
  permissions: PermissionItem[];
}

interface TopLevelGroup {
  prefix: string;
  label: string;
  icon: React.ReactNode;
  categories: CategoryGroup[];
  expanded: boolean;
}

// Permissions whose key doesn't follow `<category>_read|_write`, mapped onto the
// category they belong to. The delete grants don't repeat the segment their
// siblings carry, so without this each would be parsed as its own category
// ('course_delete') and — being absent from the sidebar-derived order — rendered
// as a lone group at the bottom of the list. They belong with the
// generation/builder permissions, which is where the delete action lives.
const CATEGORY_KEY_OVERRIDES: Record<string, string> = {
  admin_course_delete: 'course_generation',
  admin_certification_delete: 'certification_creation',
  admin_learning_path_delete: 'learning_path_creation',
};

// Parse permission_key to extract prefix and category
function parsePermissionKey(key: string): { prefix: string; category: string } {
  const override = CATEGORY_KEY_OVERRIDES[key];
  if (override) {
    return { prefix: key.startsWith('learner_') ? 'learner' : 'admin', category: override };
  }
  if (key.startsWith('admin_')) {
    const rest = key.replace('admin_', '');
    const category = rest.replace(/_read$|_write$|_delete$/, '');
    return { prefix: 'admin', category };
  }
  if (key.startsWith('learner_')) {
    const rest = key.replace('learner_', '');
    const category = rest.replace(/_read$|_write$|_delete$/, '');
    return { prefix: 'learner', category };
  }
  return { prefix: 'other', category: key };
}

// Convert snake_case to Title Case
function toTitleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Order permissions within a category by action, not by raw key — sorting on the
// key alone puts admin_course_delete ahead of admin_course_generation_read.
const ACTION_SUFFIX_ORDER = ['_read', '_write', '_delete'];

function actionRank(key: string): number {
  const index = ACTION_SUFFIX_ORDER.findIndex((suffix) => key.endsWith(suffix));
  return index === -1 ? ACTION_SUFFIX_ORDER.length : index;
}

function comparePermissions(a: PermissionItem, b: PermissionItem): number {
  const byAction = actionRank(a.permission_key) - actionRank(b.permission_key);
  return byAction !== 0 ? byAction : a.permission_key.localeCompare(b.permission_key);
}

// Extract ordered category keys from permission keys (strips prefix and _read/_write suffix)
function extractCategoryOrder(permissionKeysGroups: string[][]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const keys of permissionKeysGroups) {
    for (const key of keys) {
      const { category } = parsePermissionKey(key);
      if (!seen.has(category)) {
        seen.add(category);
        order.push(category);
      }
    }
  }
  return order;
}

// Collect all permission key groups from admin sidebar menu items in order
function collectAdminPermissionKeys(items: { permissionKeys?: string[]; children?: { permissionKeys?: string[] }[] }[]): string[][] {
  const result: string[][] = [];
  for (const item of items) {
    if (item.permissionKeys?.length) result.push(item.permissionKeys);
    if (item.children) {
      for (const child of item.children) {
        if (child.permissionKeys?.length) result.push(child.permissionKeys);
      }
    }
  }
  return result;
}

// Derive category order from sidebar configs
const ADMIN_CATEGORY_ORDER = extractCategoryOrder(collectAdminPermissionKeys(adminMenuItems));
const LEARNER_CATEGORY_ORDER = extractCategoryOrder(learnerSidebarPermissionKeys);

export default function AssignPermissionToRole() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { roleKey } = useParams<{ roleKey: string }>();
  const location = useLocation();
  const state = location.state as { from?: string; roleName?: string } | null;

  const [selectedRoleKey, setSelectedRoleKey] = useState<string | number | null>(roleKey || null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['admin', 'learner']));
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Fetch roles dropdown
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles-dropdown'],
    queryFn: () => userService.getRolesDropdown(),
  });

  // Fetch permissions from backend (search via API with debounce)
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions-all', debouncedSearch],
    queryFn: () => permissionService.listPermissions({
      skip: 0,
      limit: 1000,
      ...(debouncedSearch ? { permission_name: debouncedSearch } : {}),
    }),
  });

  // Fetch existing role permissions when a role is selected
  const { data: rolePermissionsData, isLoading: rolePermissionsLoading } = useQuery({
    queryKey: ['role-permissions', selectedRoleKey],
    queryFn: () => permissionService.getRolePermissions(selectedRoleKey as string),
    enabled: !!selectedRoleKey && selectedRoleKey !== '',
  });

  // Pre-select permissions when role permissions are loaded
  useEffect(() => {
    const permissions = rolePermissionsData?.data?.permissions || [];
    setSelectedPermissions(new Set(permissions));
  }, [rolePermissionsData]);

  // Role options for SimpleSelect with default option
  const roleOptions = useMemo(() => {
    const roles = rolesData?.data || [];
    return [
      { label: 'Select a role', value: '' },
      ...roles.map((r: any) => ({
        label: r.role_name,
        value: r.role_key,
      })),
    ];
  }, [rolesData]);

  // Permissions from API
  const filteredPermissions: PermissionItem[] = useMemo(() => {
    return permissionsData?.data || [];
  }, [permissionsData]);

  // Group permissions into top-level groups > categories
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, Record<string, PermissionItem[]>> = {};

    filteredPermissions.forEach((p) => {
      const { prefix, category } = parsePermissionKey(p.permission_key);
      if (!groups[prefix]) groups[prefix] = {};
      if (!groups[prefix][category]) groups[prefix][category] = [];
      groups[prefix][category].push(p);
    });

    const topLevelGroups: TopLevelGroup[] = [];

    if (groups.admin) {
      const categories: CategoryGroup[] = Object.entries(groups.admin)
        .sort(([a], [b]) => {
          const idxA = ADMIN_CATEGORY_ORDER.indexOf(a);
          const idxB = ADMIN_CATEGORY_ORDER.indexOf(b);
          return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
        })
        .map(([key, perms]) => ({
          category_key: `admin_${key}`,
          category_label: toTitleCase(key),
          permissions: perms.sort(comparePermissions),
        }));
      topLevelGroups.push({
        prefix: 'admin',
        label: 'Admin Site Permissions',
        icon: <Shield className="w-5 h-5" />,
        categories,
        expanded: expandedGroups.has('admin'),
      });
    }

    if (groups.learner) {
      const categories: CategoryGroup[] = Object.entries(groups.learner)
        .sort(([a], [b]) => {
          const idxA = LEARNER_CATEGORY_ORDER.indexOf(a);
          const idxB = LEARNER_CATEGORY_ORDER.indexOf(b);
          return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
        })
        .map(([key, perms]) => ({
          category_key: `learner_${key}`,
          category_label: toTitleCase(key),
          permissions: perms.sort(comparePermissions),
        }));
      topLevelGroups.push({
        prefix: 'learner',
        label: 'Learner Site Permissions',
        icon: <User className="w-5 h-5" />,
        categories,
        expanded: expandedGroups.has('learner'),
      });
    }

    return topLevelGroups;
  }, [filteredPermissions, expandedGroups]);

  // Toggle top-level group expand/collapse
  const toggleGroup = (prefix: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  };

  // Toggle category expand/collapse
  const toggleCategory = (category_key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category_key)) next.delete(category_key);
      else next.add(category_key);
      return next;
    });
  };

  // Toggle single permission
  const togglePermission = (permission_key: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permission_key)) next.delete(permission_key);
      else next.add(permission_key);
      return next;
    });
  };

  // Toggle all permissions in a category
  const toggleCategoryPermissions = (permissions: PermissionItem[]) => {
    const keys = permissions.map((p) => p.permission_key);
    const allSelected = keys.every((k) => selectedPermissions.has(k));

    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  // Toggle all permissions in a top-level group
  const toggleGroupPermissions = (group: TopLevelGroup) => {
    const keys = group.categories.flatMap((c) => c.permissions.map((p) => p.permission_key));
    const allSelected = keys.every((k) => selectedPermissions.has(k));

    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  // Toggle select/deselect all
  const allSelected = filteredPermissions.length > 0 && filteredPermissions.every((p) => selectedPermissions.has(p.permission_key));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedPermissions(new Set());
    } else {
      setSelectedPermissions(new Set(filteredPermissions.map((p) => p.permission_key)));
    }
  };

  // Check states for group/category checkboxes
  const isGroupAllSelected = (group: TopLevelGroup) => {
    const keys = group.categories.flatMap((c) => c.permissions.map((p) => p.permission_key));
    return keys.length > 0 && keys.every((k) => selectedPermissions.has(k));
  };

  const isCategoryAllSelected = (permissions: PermissionItem[]) => {
    return permissions.length > 0 && permissions.every((p) => selectedPermissions.has(p.permission_key));
  };

  // Save handler - calls assign permissions API
  const handleSave = async () => {
    if (!selectedRoleKey || selectedRoleKey === '') {
      toast.error('Please select a role first');
      return;
    }
    if (selectedPermissions.size === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    setIsSaving(true);
    try {
      await permissionService.assignPermissionsToRole(
        selectedRoleKey as string,
        Array.from(selectedPermissions)
      );
      toast.success(`${selectedPermissions.size} permissions assigned to role successfully`);
      queryClient.invalidateQueries({ queryKey: ['role-permissions', selectedRoleKey] });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.error_description || 'Failed to assign permissions');
    } finally {
      setIsSaving(false);
    }
  };

  // Expand all categories when search is active
  useEffect(() => {
    if (debouncedSearch.trim()) {
      const allCatKeys = groupedPermissions.flatMap((g) =>
        g.categories.map((c) => c.category_key)
      );
      setExpandedCategories(new Set(allCatKeys));
      setExpandedGroups(new Set(groupedPermissions.map((g) => g.prefix)));
    }
  }, [debouncedSearch, groupedPermissions]);

  // Check if all groups and categories are expanded
  const allExpanded = useMemo(() => {
    const allGroupPrefixes = groupedPermissions.map((g) => g.prefix);
    const allCatKeys = groupedPermissions.flatMap((g) => g.categories.map((c) => c.category_key));
    return (
      allGroupPrefixes.length > 0 &&
      allGroupPrefixes.every((p) => expandedGroups.has(p)) &&
      allCatKeys.length > 0 &&
      allCatKeys.every((k) => expandedCategories.has(k))
    );
  }, [groupedPermissions, expandedGroups, expandedCategories]);

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
      setExpandedCategories(new Set());
    } else {
      setExpandedGroups(new Set(groupedPermissions.map((g) => g.prefix)));
      setExpandedCategories(
        new Set(groupedPermissions.flatMap((g) => g.categories.map((c) => c.category_key)))
      );
    }
  };

  const totalPermissions = filteredPermissions.length;
  const selectedCount = selectedPermissions.size;
  const isLoading = rolesLoading || permissionsLoading || rolePermissionsLoading;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(state?.from || '/admin/roles')}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Assign Permissions to Role</h1>
            <p className="text-brand-purple-100 text-sm mt-1">
              Select permissions to assign to a role for access control
            </p>
          </div>
        </div>
      </div>

      {/* Role Selector + Save + Search */}
      <div className="card p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex-1 w-full sm:max-w-xs">
            <SimpleSelect
              label="Select Role"
              required
              options={roleOptions}
              value={selectedRoleKey as string}
              onChange={(val) => setSelectedRoleKey(val)}
              placeholder="Choose a role..."
              isLoading={rolesLoading}
            />
          </div>
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search permissions by name..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Permissions Table */}
      {isLoading ? (
        <div className="card p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      ) : filteredPermissions.length === 0 ? (
        <div className="card p-8 text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No permissions found matching your search.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Table header with select/deselect */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <button onClick={toggleSelectAll} className="flex-shrink-0">
                {allSelected ? (
                  <CheckSquare className="w-5 h-5 text-brand-purple-600" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <span className="text-sm font-medium text-gray-700">
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
            </div>
            <button
              onClick={toggleExpandAll}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 px-2.5 py-1.5 rounded-md hover:bg-gray-200 transition-colors"
            >
              {allExpanded ? (
                <>
                  <ChevronsDownUp className="w-3.5 h-3.5" />
                  Collapse All
                </>
              ) : (
                <>
                  <ChevronsUpDown className="w-3.5 h-3.5" />
                  Expand All
                </>
              )}
            </button>
          </div>

          {groupedPermissions.map((group) => (
            <div key={group.prefix} className="border-b border-gray-200 last:border-b-0">
              {/* Top-level group header */}
              <div
                className="flex items-center gap-3 px-4 sm:px-6 py-3.5 cursor-pointer select-none bg-gray-50 hover:bg-gray-100 transition-colors"
                onClick={() => toggleGroup(group.prefix)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroupPermissions(group);
                  }}
                  className="flex-shrink-0"
                >
                  {isGroupAllSelected(group) ? (
                    <CheckSquare className="w-5 h-5 text-brand-purple-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {expandedGroups.has(group.prefix) ? (
                  <ChevronDown className="w-5 h-5 text-gray-700" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                )}
                <span className="text-gray-700">
                  {group.icon}
                </span>
                <span className="font-semibold text-sm sm:text-base text-gray-900">
                  {group.label}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full ml-auto bg-gray-200 text-gray-700">
                  {group.categories.reduce((sum, c) => sum + c.permissions.length, 0)} permissions
                </span>
              </div>

              {/* Categories within group */}
              {expandedGroups.has(group.prefix) && (
                <div>
                  {group.categories.map((category) => (
                    <div key={category.category_key} className="border-t border-gray-100">
                      {/* Category header */}
                      <div
                        className="flex items-center gap-3 px-6 sm:px-10 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => toggleCategory(category.category_key)}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryPermissions(category.permissions);
                          }}
                          className="flex-shrink-0"
                        >
                          {isCategoryAllSelected(category.permissions) ? (
                            <CheckSquare className="w-4 h-4 text-brand-purple-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        {expandedCategories.has(category.category_key) ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="font-medium text-sm text-gray-700">
                          {category.category_label}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {category.permissions.filter((p) => selectedPermissions.has(p.permission_key)).length}/{category.permissions.length}
                        </span>
                      </div>

                      {/* Permission rows within category */}
                      {expandedCategories.has(category.category_key) && (
                        <div className="divide-y divide-gray-50">
                          {category.permissions.map((permission) => (
                            <div
                              key={permission.permission_key}
                              className={`flex items-center gap-3 px-8 sm:px-14 py-2.5 cursor-pointer transition-colors ${
                                selectedPermissions.has(permission.permission_key)
                                  ? 'bg-brand-purple-50 hover:bg-brand-purple-100'
                                  : 'hover:bg-gray-50'
                              }`}
                              onClick={() => togglePermission(permission.permission_key)}
                            >
                              <button className="flex-shrink-0">
                                {selectedPermissions.has(permission.permission_key) ? (
                                  <CheckSquare className="w-4 h-4 text-brand-purple-600" />
                                ) : (
                                  <Square className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-800">
                                  {permission.permission_name}
                                </span>
                                {permission.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                                    {permission.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bottom bar with count + Save */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-brand-purple-600">{selectedCount}</span> of{' '}
          <span className="font-semibold">{totalPermissions}</span> permissions selected
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || !selectedRoleKey || selectedPermissions.size === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {isSaving ? (
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}
